/**
 * API Error Handling
 *
 * Centralized error classes and utilities for handling API failures.
 * Provides consistent error responses for both SOAP and REST APIs.
 */

import { ApiErrorResponse, ApiResponse } from './types';

// ============================================================================
// Error Codes
// ============================================================================

export const API_ERROR_CODES = {
  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  CONNECTION_REFUSED: 'CONNECTION_REFUSED',

  // Authentication errors
  UNAUTHORIZED: 'UNAUTHORIZED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  MISSING_CREDENTIALS: 'MISSING_CREDENTIALS',

  // Request errors
  BAD_REQUEST: 'BAD_REQUEST',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',

  // Server errors
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  GATEWAY_TIMEOUT: 'GATEWAY_TIMEOUT',

  // SOAP-specific errors
  SOAP_FAULT: 'SOAP_FAULT',
  XML_PARSE_ERROR: 'XML_PARSE_ERROR',
  INVALID_SOAP_RESPONSE: 'INVALID_SOAP_RESPONSE',

  // Rate limiting
  RATE_LIMITED: 'RATE_LIMITED',

  // Email verification
  VERIFICATION_TOKEN_EXPIRED: 'VERIFICATION_TOKEN_EXPIRED',
  VERIFICATION_TOKEN_INVALID: 'VERIFICATION_TOKEN_INVALID',
  EMAIL_ALREADY_VERIFIED: 'EMAIL_ALREADY_VERIFIED',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',

  // Password reset
  RESET_TOKEN_EXPIRED: 'RESET_TOKEN_EXPIRED',
  RESET_TOKEN_INVALID: 'RESET_TOKEN_INVALID',

  // Constituent matching
  CONSTITUENT_NOT_FOUND: 'CONSTITUENT_NOT_FOUND',
  CONSTITUENT_AMBIGUOUS: 'CONSTITUENT_AMBIGUOUS',

  // Booking errors
  BOOKING_REFUNDED: 'BOOKING_REFUNDED',
  BOOKING_HAS_DISCOUNT: 'BOOKING_HAS_DISCOUNT',

  // Guest pass errors
  PASS_SENT_NOT_RECORDED: 'PASS_SENT_NOT_RECORDED',

  // Generic errors
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES];

// ============================================================================
// Base API Error Class
// ============================================================================

/**
 * Base error class for all API-related errors
 */
export class ApiError extends Error {
  public readonly code: ApiErrorCode;
  public readonly statusCode?: number;
  public readonly details?: string;
  public readonly originalError?: unknown;
  public readonly timestamp: string;
  public readonly retryable: boolean;

  constructor(
    message: string,
    code: ApiErrorCode,
    options?: {
      statusCode?: number;
      details?: string;
      originalError?: unknown;
      retryable?: boolean;
    }
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.statusCode = options?.statusCode;
    this.details = options?.details;
    this.originalError = options?.originalError;
    this.timestamp = new Date().toISOString();
    this.retryable = options?.retryable ?? this.isRetryableError(code);

    // Maintain proper stack trace in V8 environments
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Determine if an error code represents a retryable error
   */
  private isRetryableError(code: ApiErrorCode): boolean {
    const retryableCodes: ApiErrorCode[] = [
      API_ERROR_CODES.NETWORK_ERROR,
      API_ERROR_CODES.TIMEOUT,
      API_ERROR_CODES.CONNECTION_REFUSED,
      API_ERROR_CODES.SERVICE_UNAVAILABLE,
      API_ERROR_CODES.GATEWAY_TIMEOUT,
      API_ERROR_CODES.RATE_LIMITED,
    ];
    return retryableCodes.includes(code);
  }

  /**
   * Convert to API error response format
   */
  toErrorResponse(): ApiErrorResponse {
    return {
      code: this.code,
      message: this.message,
      details: this.details,
      originalError:
        process.env.NODE_ENV === 'development' ? this.originalError : undefined,
    };
  }

  /**
   * Convert to full API response format
   */
  toApiResponse<T>(): ApiResponse<T> {
    return {
      success: false,
      error: this.toErrorResponse(),
      metadata: {
        timestamp: this.timestamp,
        source: 'rest', // Will be overridden by specific handlers
      },
    };
  }
}

// ============================================================================
// Specific Error Classes
// ============================================================================

/**
 * Network-related errors (timeouts, connection issues)
 */
export class NetworkError extends ApiError {
  constructor(message: string, originalError?: unknown) {
    super(message, API_ERROR_CODES.NETWORK_ERROR, {
      originalError,
      retryable: true,
    });
    this.name = 'NetworkError';
  }
}

/**
 * Timeout errors
 */
export class TimeoutError extends ApiError {
  constructor(timeoutMs: number, originalError?: unknown) {
    super(
      `Request timed out after ${timeoutMs}ms`,
      API_ERROR_CODES.TIMEOUT,
      {
        originalError,
        retryable: true,
        details: `The request did not complete within the ${timeoutMs}ms timeout period`,
      }
    );
    this.name = 'TimeoutError';
  }
}

/**
 * Authentication errors
 */
export class AuthenticationError extends ApiError {
  constructor(
    message: string,
    code: ApiErrorCode = API_ERROR_CODES.UNAUTHORIZED,
    originalError?: unknown
  ) {
    super(message, code, {
      statusCode: 401,
      originalError,
      retryable: code === API_ERROR_CODES.TOKEN_EXPIRED,
    });
    this.name = 'AuthenticationError';
  }
}

/**
 * SOAP-specific errors
 */
export class SoapError extends ApiError {
  public readonly faultCode?: string;
  public readonly faultString?: string;

  constructor(
    message: string,
    options?: {
      faultCode?: string;
      faultString?: string;
      details?: string;
      originalError?: unknown;
    }
  ) {
    super(message, API_ERROR_CODES.SOAP_FAULT, {
      details: options?.details || options?.faultString,
      originalError: options?.originalError,
      retryable: false,
    });
    this.name = 'SoapError';
    this.faultCode = options?.faultCode;
    this.faultString = options?.faultString;
  }
}

/**
 * XML parsing errors
 */
export class XmlParseError extends ApiError {
  constructor(message: string, originalError?: unknown) {
    super(message, API_ERROR_CODES.XML_PARSE_ERROR, {
      originalError,
      retryable: false,
    });
    this.name = 'XmlParseError';
  }
}

/**
 * Rate limiting errors
 */
export class RateLimitError extends ApiError {
  public readonly retryAfter?: number;

  constructor(retryAfter?: number, originalError?: unknown) {
    const message = retryAfter
      ? `Rate limited. Retry after ${retryAfter} seconds`
      : 'Rate limited. Please try again later';

    super(message, API_ERROR_CODES.RATE_LIMITED, {
      statusCode: 429,
      originalError,
      retryable: true,
    });
    this.name = 'RateLimitError';
    this.retryAfter = retryAfter;
  }
}

/**
 * Validation errors for request data
 */
export class ValidationError extends ApiError {
  public readonly validationErrors: Record<string, string>;

  constructor(
    message: string,
    validationErrors: Record<string, string>,
    originalError?: unknown
  ) {
    super(message, API_ERROR_CODES.VALIDATION_ERROR, {
      statusCode: 400,
      details: Object.entries(validationErrors)
        .map(([field, error]) => `${field}: ${error}`)
        .join('; '),
      originalError,
      retryable: false,
    });
    this.name = 'ValidationError';
    this.validationErrors = validationErrors;
  }
}

// ============================================================================
// Error Factory Functions
// ============================================================================

/**
 * Create an error from an HTTP status code
 */
export function createErrorFromStatus(
  statusCode: number,
  message?: string,
  originalError?: unknown
): ApiError {
  switch (statusCode) {
    case 400:
      return new ApiError(
        message || 'Bad Request',
        API_ERROR_CODES.BAD_REQUEST,
        { statusCode, originalError }
      );
    case 401:
      return new AuthenticationError(
        message || 'Unauthorized',
        API_ERROR_CODES.UNAUTHORIZED,
        originalError
      );
    case 403:
      return new AuthenticationError(
        message || 'Forbidden',
        API_ERROR_CODES.UNAUTHORIZED,
        originalError
      );
    case 404:
      return new ApiError(
        message || 'Not Found',
        API_ERROR_CODES.NOT_FOUND,
        { statusCode, originalError }
      );
    case 409:
      return new ApiError(
        message || 'Conflict',
        API_ERROR_CODES.CONFLICT,
        { statusCode, originalError }
      );
    case 429:
      return new RateLimitError(undefined, originalError);
    case 500:
      return new ApiError(
        message || 'Internal Server Error',
        API_ERROR_CODES.INTERNAL_SERVER_ERROR,
        { statusCode, originalError, retryable: true }
      );
    case 502:
    case 503:
      return new ApiError(
        message || 'Service Unavailable',
        API_ERROR_CODES.SERVICE_UNAVAILABLE,
        { statusCode, originalError, retryable: true }
      );
    case 504:
      return new ApiError(
        message || 'Gateway Timeout',
        API_ERROR_CODES.GATEWAY_TIMEOUT,
        { statusCode, originalError, retryable: true }
      );
    default:
      return new ApiError(
        message || `HTTP Error ${statusCode}`,
        API_ERROR_CODES.UNKNOWN_ERROR,
        { statusCode, originalError }
      );
  }
}

/**
 * Create an error from a caught exception
 */
export function createErrorFromException(error: unknown): ApiError {
  // Already an ApiError
  if (error instanceof ApiError) {
    return error;
  }

  // Native Error object
  if (error instanceof Error) {
    const message = error.message;

    // Check for common error patterns
    if (message.includes('timeout') || message.includes('ETIMEDOUT')) {
      return new TimeoutError(30000, error);
    }
    if (
      message.includes('network') ||
      message.includes('ECONNREFUSED') ||
      message.includes('ENOTFOUND')
    ) {
      return new NetworkError(message, error);
    }
    if (message.includes('abort') || message.includes('cancelled')) {
      return new ApiError(
        'Request was cancelled',
        API_ERROR_CODES.NETWORK_ERROR,
        { originalError: error }
      );
    }

    // Prisma / database errors — never expose internals to clients
    if (
      error.constructor?.name?.startsWith('Prisma') ||
      message.includes('prisma') ||
      message.includes('PrismaClient') ||
      message.includes('Invalid `prisma.')
    ) {
      return new ApiError(
        'Something went wrong. Please try again.',
        API_ERROR_CODES.INTERNAL_SERVER_ERROR,
        { originalError: error }
      );
    }

    return new ApiError(message, API_ERROR_CODES.UNKNOWN_ERROR, {
      originalError: error,
    });
  }

  // Unknown error type
  return new ApiError(
    'An unknown error occurred',
    API_ERROR_CODES.UNKNOWN_ERROR,
    { originalError: error }
  );
}

// ============================================================================
// Error Handling Utilities
// ============================================================================

/**
 * Wrap an async function with error handling
 */
export async function withErrorHandling<T>(
  fn: () => Promise<T>,
  context?: string
): Promise<ApiResponse<T>> {
  try {
    const data = await fn();
    return {
      success: true,
      data,
      metadata: {
        timestamp: new Date().toISOString(),
        source: 'rest',
      },
    };
  } catch (error) {
    const apiError = createErrorFromException(error);
    console.error(
      `[API Error]${context ? ` [${context}]` : ''}: ${apiError.message}`,
      {
        code: apiError.code,
        details: apiError.details,
      }
    );
    return apiError.toApiResponse<T>();
  }
}

/**
 * Log error with consistent formatting
 */
export function logError(
  error: ApiError | Error,
  context?: string
): void {
  const apiError =
    error instanceof ApiError ? error : createErrorFromException(error);

  const logData = {
    timestamp: new Date().toISOString(),
    context,
    code: apiError.code,
    message: apiError.message,
    details: apiError.details,
    retryable: apiError.retryable,
    ...(process.env.NODE_ENV === 'development' && {
      stack: apiError.stack,
    }),
  };

  console.error('[API Error]', JSON.stringify(logData, null, 2));
}

/**
 * Check if an error is a specific type
 */
export function isErrorCode(error: unknown, code: ApiErrorCode): boolean {
  return error instanceof ApiError && error.code === code;
}

/**
 * Check if an error is retryable
 */
export function isRetryable(error: unknown): boolean {
  return error instanceof ApiError && error.retryable;
}
